import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { EnumCellProps, JsonSchema } from '@jsonforms/core';
import merge from 'lodash/merge';
import { SpectrumInputProps } from './index';
import { DimensionValue } from '@react-types/shared';
import { ComboBox, Item, Text, useFilter } from '@adobe/react-spectrum';
import { useTreeData } from 'react-stately';
import Close from '@spectrum-icons/workflow/Close';
import { useIsMobileDevice } from '@react-spectrum/utils';
import { convertToIdNameFormat } from '../util/commonUtils';
import './InputComboBox.css';

interface IComboBoxOptions extends EnumCellProps {
  options: {
    label: string;
    value: any;
    description?: string;
  }[];
}

export const InputComboBox = (props: SpectrumInputProps & IComboBoxOptions) => {
  const {
    config,
    uischema,
    options,
    data,
    // isValid,
    id,
    enabled,
    required,
    path,
    handleChange,
    label,
    placeholder,
    validationState,
    errorMessage,
    description,
  } = props;

  const appliedUiSchemaOptions = merge({}, config, uischema.options);
  const isRequired = required && !appliedUiSchemaOptions.hideRequiredAsterisk;

  const spectrumProps = uischema?.options?.spectrumProps;

  const width: DimensionValue =
    uischema.options?.spectrumProps?.width || '100%';

  const findEnumSchema = (schemas: JsonSchema[]) =>
    schemas.find(
      (s) =>
        s.enum !== undefined && (s.type === 'string' || s.type === undefined)
    );

  const tryEnumSchema = (anyOf: JsonSchema[]) => {
    const enumSchema = findEnumSchema(anyOf);
    return enumSchema.enum.map((v) => {
      return { value: v, label: v, description: '', icon: '' };
    });
  };
  const items = options ?? tryEnumSchema(props.schema.anyOf);

  const comboBoxOptions = convertToIdNameFormat(items);

  let [showAll, setShowAll] = React.useState(false);
  let [filterValue, setFilterValue] = React.useState('');
  let { contains } = useFilter({ sensitivity: 'base' });
  let filteredItems = React.useMemo(
    () => comboBoxOptions.filter((item) => contains(item.name, filterValue)),
    [comboBoxOptions, filterValue]
  );

  // Find id using data
  const selectedItem = comboBoxOptions.find((item) => item.id === data);

  let [fieldState, setFieldState] = React.useState({
    selectedKey: data,
    inputValue: selectedItem?.name ?? '',
  });

  let list = useTreeData({
    initialItems: comboBoxOptions,
  });

  let onSelectionChange = (key: React.Key) => {
    handleChange(path, key);
    setFieldState({
      inputValue: list.getItem(key)?.value.name ?? '',
      selectedKey: key,
    });
  };

  let onInputChange = (value: any) => {
    setShowAll(false);
    setFilterValue(value);
    setFieldState((prevState) => ({
      inputValue: value,
      selectedKey: value === '' ? null : prevState.selectedKey,
    }));
  };

  const clearInput = () => {
    handleChange(path, '');

    setFieldState({
      inputValue: '',
      selectedKey: null,
    });
  };

  useEffect(() => {
    // Sync the data change from outside
    if (data !== fieldState.selectedKey) {
      handleChange(path, data ?? '');

      setFieldState(() => ({
        inputValue: selectedItem?.name ?? '',
        selectedKey: data,
      }));
    }
  }, [data]);

  let isMobile = useIsMobileDevice();

  const [clearTop, setClearTop] = useState(24);

  useLayoutEffect(() => {
    /**
     * Dynamically calculate the top position of the clear button based on the height of the label
     */
    const cbWrapperElement = cbWrapper.current;
    if (cbWrapperElement) {
      const labelElement = cbWrapperElement.querySelector('label');
      if (labelElement) {
        const labelHeight = labelElement.clientHeight;
        setClearTop(labelHeight + 2);
      }
    }
  }, []);

  const cbWrapper = useRef(null);

  return (
    <div className='cb-wrapper' ref={cbWrapper}>
      {!isMobile && fieldState.inputValue && (
        <button
          className='cb-clear-button'
          style={{ top: `${clearTop}px` }}
          onClick={clearInput}
        >
          <Close size='XS' />
        </button>
      )}
      <ComboBox
        key={id}
        id={id}
        {...(!uischema?.options?.hideLabel && { label: label })}
        aria-label='comboBox'
        isRequired={isRequired}
        isDisabled={!enabled}
        width={width}
        defaultItems={comboBoxOptions}
        items={showAll ? comboBoxOptions : filteredItems}
        selectedKey={fieldState.selectedKey ?? ''}
        inputValue={fieldState.inputValue}
        onSelectionChange={onSelectionChange}
        onInputChange={onInputChange}
        placeholder={placeholder}
        validationState={validationState}
        errorMessage={errorMessage}
        description={description}
        onOpenChange={(isOpen, menuTrigger) => {
          // Show all items if menu is opened manually
          // i.e. by the arrow keys or trigger button
          if (menuTrigger === 'manual' && isOpen) {
            setShowAll(true);
          }
        }}
        {...spectrumProps}
      >
        {(item: any) => (
          <Item key={item.id} textValue={item.name}>
            {item.description ? (
              <>
                <Text>{item.name}</Text>
                <Text slot='description'>{item.description}</Text>
              </>
            ) : (
              item.name
            )}
          </Item>
        )}
      </ComboBox>
    </div>
  );
};
